// The editor: document + camera + selection + tools, independent of React.
import { generateKeyBetween, generateNKeysBetween } from "fractional-indexing";
import type {
  Align, BlendMode, BoardBackground, Dash, DrawElement, FillStyle, FontKey, HeadKind, LayerElement, PageElement, PatternKey, PenKind, Route,
  SepiaElement, ShapeKind
} from "../../shared/model";
import { BASE_LAYER, DEFAULT_BG, PAGE_GAP } from "../../shared/model";
import { Camera } from "./camera";
import { InputLog } from "./inputlog";
import { Doc, History, type Change, type Origin, type Patch } from "./doc";
import {
  bounds, boxOf, elementText, frameBox, hitHollow, hitTest, intersectsBox, newId, routeConnector, transformElement, type Xform
} from "./elements";
import { type Box, type Vec, boxContains, center, unionBoxes } from "./geometry";
import { PENS } from "./ink";
import { INKS, PAPERS } from "./style";

export type ToolId =
  | "select" | "hand" | "pen" | "highlighter" | "eraser" | "lasso" | "shape" | "line" | "text" | "note" | "card"
  | "frame" | "sticker" | "laser";

export interface EditorSettings {
  /** pencil-only drawing: "auto" switches on the first time an Apple Pencil touches the canvas */
  penMode: "auto" | "on" | "off";
  penSeen: boolean;
  /** "keyboard": a tap starts typing there and fingers only move the page (for phones, or an iPad with a keyboard) */
  writeWith: "pen" | "keyboard";
  leftHanded: boolean;
  pressureGamma: number;
  snapShapes: boolean;
  scribbleErase: boolean;
  fingerUndo: boolean;
  toolLock: boolean;
  snapToGrid: boolean;
}

export interface StyleState {
  pen: PenKind;
  inkColor: string;
  hlColor: string;
  penSize: Record<PenKind, number>;
  eraserMode: "stroke" | "precise";
  eraserSize: number;
  /** erase on every open layer instead of only the active one */
  eraseAllLayers: boolean;
  shapeKind: ShapeKind;
  stroke: string;
  fill: string;
  fillStyle: FillStyle;
  strokeWidth: number;
  dash: Dash;
  rough: number;
  route: Route;
  startHead: HeadKind;
  endHead: HeadKind;
  font: FontKey;
  fontSize: number;
  textColor: string;
  align: Align;
  notePaper: string;
  tape: string | null;
  cardPaper: string;
  stickerKey: string | null;
  shadow: boolean;
}

export interface UiState {
  tool: ToolId;
  selection: string[];
  editingId: string | null;
  hoverId: string | null;
  readOnly: boolean;
  /** a transform gesture is running (hide the inspector, etc.) */
  busy: boolean;
  /** members of the group the user has double-tapped into */
  enteredGroup: string | null;
  /** new things are drawn on this layer */
  activeLayer: string;
}

export interface Overlay {
  marquee: Box | null;
  lasso: Vec[] | null;
  eraser: { p: Vec; r: number } | null;
  guides: { x?: number; y?: number }[];
  laser: { pts: { p: Vec; t: number }[] } | null;
  snapPreview: string | null;
  hoverPen: { p: Vec; r: number; color: string } | null;
  anchorHint: { id: string; p: Vec } | null;
}

export interface PointerInfo {
  id: number;
  kind: "pen" | "touch" | "mouse";
  screen: Vec;
  world: Vec;
  pressure: number;
  hasPressure: boolean;
  shift: boolean;
  alt: boolean;
  meta: boolean;
  button: number;
  time: number;
  /** pen barrel eraser (Surface pens) */
  eraserButton?: boolean;
}

export interface Tool {
  readonly id: ToolId;
  onDown(p: PointerInfo): void;
  onMove(p: PointerInfo, coalesced: PointerInfo[], predicted: PointerInfo[]): void;
  onUp(p: PointerInfo): void;
  onCancel(): void;
  onHover?(p: PointerInfo | null): void;
  /** a quick finger tap while fingers only navigate (pencil mode) */
  onTap?(p: PointerInfo): void;
  activate?(): void;
  deactivate?(): void;
  cursor(): string;
}

/** Services the editor needs from the page that hosts it. */
export interface EditorHost {
  uploadImage(file: Blob): Promise<{ src: string; w: number; h: number }>;
  resolveImage(src: string): string;
  openBoardLink(title: string): void;
  toast(msg: string, kind?: "info" | "error"): void;
}

const CLIP_MARK = "sepia/clipboard/v1";

export const DEFAULT_STYLE: StyleState = {
  pen: "fountain",
  inkColor: INKS[0].color,
  hlColor: "#f3d36b",
  penSize: { fountain: PENS.fountain.size, ballpoint: PENS.ballpoint.size, pencil: PENS.pencil.size, marker: PENS.marker.size, highlighter: PENS.highlighter.size, brush: PENS.brush.size },
  eraserMode: "stroke",
  eraserSize: 14,
  eraseAllLayers: false,
  shapeKind: "rect",
  stroke: INKS[0].color,
  fill: "#f4d98c",
  fillStyle: "none",
  strokeWidth: 2.2,
  dash: "solid",
  rough: 0.35,
  route: "straight",
  startHead: "none",
  endHead: "arrow",
  font: "hand",
  fontSize: 28,
  textColor: INKS[0].color,
  align: "left",
  notePaper: "#f6dc8f",
  tape: "#d98c8c",
  cardPaper: "#fbf6ea",
  stickerKey: null,
  shadow: true
};

export const DEFAULT_SETTINGS: EditorSettings = {
  penMode: "auto",
  penSeen: false,
  writeWith: "pen",
  leftHanded: false,
  pressureGamma: 0.85,
  snapShapes: true,
  scribbleErase: true,
  fingerUndo: true,
  toolLock: false,
  snapToGrid: false
};

type Listener = () => void;

export class Editor {
  readonly doc = new Doc();
  readonly history = new History();
  readonly camera = new Camera();
  /** recent touches, for touch reports */
  readonly inputLog = new InputLog();
  ui: UiState = { tool: "pen", selection: [], editingId: null, hoverId: null, readOnly: false, busy: false, enteredGroup: null, activeLayer: BASE_LAYER };
  style: StyleState = structuredClone(DEFAULT_STYLE);
  settings: EditorSettings = { ...DEFAULT_SETTINGS };
  overlay: Overlay = { marquee: null, lasso: null, eraser: null, guides: [], laser: null, snapPreview: null, hoverPen: null, anchorHint: null };
  bg: BoardBackground = { ...DEFAULT_BG };
  host: EditorHost;
  tools = new Map<ToolId, Tool>();
  /** temporary versions of elements while a gesture runs (not in the document yet) */
  private transient = new Map<string, SepiaElement>();
  private uiListeners = new Set<Listener>();
  private styleListeners = new Set<Listener>();
  private overlayListeners = new Set<Listener>();
  private elListeners = new Map<string, Set<Listener>>();
  private transientListeners = new Set<Listener>();
  private clipboard: SepiaElement[] = [];
  /** DOM node of the live (unfinished) ink stroke, driven directly for the lowest latency */
  livePath: SVGPathElement | null = null;
  /** screen-space container, set by the canvas component */
  viewport: HTMLElement | null = null;
  /**
   * iPadOS only shows the keyboard for focus() calls made while handling a tap. startEditing() focuses this
   * hidden textarea synchronously; the real editor box takes focus from it a moment later.
   */
  focusProxy: (() => void) | null = null;
  /** the last text box typed into, so typing on in keyboard mode continues on the line below it */
  lastTextId: string | null = null;
  /** when a text box last closed: keys pressed right after are late typing, not shortcuts */
  editEndedAt = -Infinity;

  constructor(host: EditorHost) {
    this.host = host;
    this.doc.subscribe((c) => this.onDocChange(c));
    // a notebook can't be scrolled away from its pages
    this.camera.limit = () => this.pagesBox();
  }

  // ---------- subscriptions ----------

  subscribeUi(fn: Listener) {
    this.uiListeners.add(fn);
    return () => this.uiListeners.delete(fn);
  }
  subscribeStyle(fn: Listener) {
    this.styleListeners.add(fn);
    return () => this.styleListeners.delete(fn);
  }
  subscribeOverlay(fn: Listener) {
    this.overlayListeners.add(fn);
    return () => this.overlayListeners.delete(fn);
  }
  subscribeTransient(fn: Listener) {
    this.transientListeners.add(fn);
    return () => this.transientListeners.delete(fn);
  }
  /** Fires when the element changes in the document or in a running gesture. */
  subscribeElement(id: string, fn: Listener) {
    let set = this.elListeners.get(id);
    if (!set) this.elListeners.set(id, (set = new Set()));
    set.add(fn);
    const off = this.doc.subscribeElement(id, fn);
    return () => {
      set!.delete(fn);
      if (!set!.size) this.elListeners.delete(id);
      off();
    };
  }

  setUi(patch: Partial<UiState>): void {
    let changed = false;
    for (const k in patch) {
      const key = k as keyof UiState;
      if (this.ui[key] !== patch[key]) changed = true;
    }
    if (!changed) return;
    this.ui = { ...this.ui, ...patch };
    for (const l of this.uiListeners) l();
  }

  setOverlay(patch: Partial<Overlay>): void {
    this.overlay = { ...this.overlay, ...patch };
    for (const l of this.overlayListeners) l();
  }

  /** Current element as rendered (includes gesture previews). */
  getEl(id: string): SepiaElement | undefined {
    return this.transient.get(id) ?? this.doc.get(id);
  }

  setTransient(els: SepiaElement[] | null): void {
    const touched = new Set<string>(this.transient.keys());
    this.transient.clear();
    if (els) for (const e of els) {
      this.transient.set(e.id, e);
      touched.add(e.id);
    }
    for (const id of touched) {
      const set = this.elListeners.get(id);
      if (set) for (const l of set) l();
    }
    for (const l of this.transientListeners) l();
  }

  hasTransient(): boolean {
    return this.transient.size > 0;
  }

  /** Previews that are not in the document yet (a shape being dragged out, a snapped shape). */
  draftIds(): string[] {
    const out: string[] = [];
    for (const id of this.transient.keys()) if (!this.doc.has(id)) out.push(id);
    return out;
  }

  // ---------- style & settings ----------

  setStyle(patch: Partial<StyleState>): void {
    this.style = { ...this.style, ...patch };
    for (const l of this.styleListeners) l();
  }

  setSettings(patch: Partial<EditorSettings>): void {
    this.settings = { ...this.settings, ...patch };
    for (const l of this.uiListeners) l();
    try {
      localStorage.setItem("sepia.settings", JSON.stringify(this.settings));
    } catch {
      /* private mode */
    }
  }

  /** Fingers navigate and only the pencil draws. */
  get pencilOnly(): boolean {
    return this.settings.penMode === "on" || (this.settings.penMode === "auto" && this.settings.penSeen);
  }

  /** Writing with the keyboard: the text tool stays on, a tap types there, fingers pan and pinch. */
  get keyboardMode(): boolean {
    return this.settings.writeWith === "keyboard" && this.ui.tool === "text";
  }

  /** One finger moves the page rather than drawing. */
  get fingersNavigate(): boolean {
    return this.pencilOnly || this.keyboardMode;
  }

  setWriteWith(mode: EditorSettings["writeWith"]): void {
    this.setSettings({ writeWith: mode });
    this.setTool(mode === "keyboard" ? "text" : "pen");
  }

  setBackground(bg: BoardBackground): void {
    const wasDark = PAPERS[this.bg.paper]?.dark;
    this.bg = bg;
    const dark = PAPERS[bg.paper]?.dark;
    if (dark !== wasDark) {
      const ink = PAPERS[bg.paper].ink;
      this.setStyle({ inkColor: ink, stroke: ink, textColor: ink });
    }
    for (const l of this.uiListeners) l();
  }

  // ---------- tools ----------

  registerTool(t: Tool): void {
    this.tools.set(t.id, t);
  }

  get tool(): Tool {
    return this.tools.get(this.ui.tool)!;
  }

  setTool(id: ToolId): void {
    if (this.ui.readOnly && !["select", "hand", "laser"].includes(id)) return;
    if (id === this.ui.tool) return;
    // picking any other tool leaves keyboard mode
    if (id !== "text" && this.settings.writeWith === "keyboard") this.setSettings({ writeWith: "pen" });
    this.tool?.deactivate?.();
    this.stopEditing();
    const keepSelection = id === "select" || id === "lasso";
    this.setUi({ tool: id, selection: keepSelection ? this.ui.selection : [], hoverId: null });
    this.setOverlay({ hoverPen: null, eraser: null, anchorHint: null });
    this.tools.get(id)?.activate?.();
  }

  // ---------- document changes ----------

  private onDocChange(c: Change): void {
    // drop selection entries that no longer exist
    if (this.ui.selection.some((id) => !this.doc.has(id))) {
      this.setUi({ selection: this.ui.selection.filter((id) => this.doc.has(id)) });
    }
    if (this.ui.editingId && !this.doc.has(this.ui.editingId)) this.setUi({ editingId: null });
    if (c.origin === "remote" || c.origin === "load") return;
  }

  /**
   * Apply a local edit: re-routes attached connectors, records undo (unless history: false) and lets sync send it.
   */
  commit(patch: Patch, opts: { history?: boolean; key?: string; origin?: Origin } = {}): Change | null {
    if (this.ui.readOnly) return null;
    const puts = [...(patch.puts || [])];
    const dels = new Set(patch.dels || []);
    // connectors attached to moved elements follow them
    const changed = new Set(puts.map((e) => e.id));
    if (changed.size || dels.size) {
      const get = (id: string) => (dels.has(id) ? undefined : puts.find((p) => p.id === id) ?? this.doc.get(id));
      for (const el of this.doc.all()) {
        if (el.type !== "line" || dels.has(el.id)) continue;
        const a = el.start.bind;
        const b = el.end.bind;
        if ((a && (changed.has(a) || dels.has(a))) || (b && (changed.has(b) || dels.has(b)))) {
          const base = (puts.find((p) => p.id === el.id) as typeof el | undefined) ?? el;
          let next = base;
          if (a && dels.has(a)) next = { ...next, start: { ...next.start, bind: null } };
          if (b && dels.has(b)) next = { ...next, end: { ...next.end, bind: null } };
          next = routeConnector(next, get);
          if (next !== base) {
            const i = puts.findIndex((p) => p.id === el.id);
            if (i >= 0) puts[i] = next;
            else puts.push(next);
          }
        }
      }
    }
    // connectors written with bindings (pasted, restored, created in code) are routed to their targets too
    for (let i = 0; i < puts.length; i++) {
      const el = puts[i];
      if (el.type === "line" && (el.start.bind || el.end.bind)) {
        const get = (id: string) => (dels.has(id) ? undefined : puts.find((p) => p.id === id) ?? this.doc.get(id));
        puts[i] = routeConnector(el, get);
      }
    }
    const change = this.doc.apply({ puts, dels: [...dels] }, opts.origin ?? "local");
    if (change && opts.history !== false) this.history.record(change, this.doc, opts.key, this.ui.selection);
    return change;
  }

  undo(): void {
    if (this.ui.readOnly) return;
    this.stopEditing();
    const r = this.history.undo(this.doc);
    if (r) this.setUi({ selection: (r.selection ?? []).filter((id) => this.doc.has(id)) });
  }

  redo(): void {
    if (this.ui.readOnly) return;
    this.stopEditing();
    this.history.redo(this.doc);
  }

  // ---------- z-order ----------

  /** n fresh z keys above everything. */
  zTop(n = 1): string[] {
    return generateNKeysBetween(this.doc.topZ(), null, n);
  }

  zBottom(n = 1): string[] {
    return generateNKeysBetween(null, this.doc.bottomZ(), n);
  }

  // ---------- selection ----------

  selected(): SepiaElement[] {
    return this.ui.selection.map((id) => this.getEl(id)).filter(Boolean) as SepiaElement[];
  }

  /** Expand ids to whole groups (unless we are inside that group). */
  expandGroups(ids: string[]): string[] {
    const out = new Set(ids);
    const groups = new Set<string>();
    for (const id of ids) {
      const g = this.doc.get(id)?.groupId;
      if (g && g !== this.ui.enteredGroup) groups.add(g);
    }
    if (groups.size) for (const el of this.doc.all()) if (el.groupId && groups.has(el.groupId)) out.add(el.id);
    return [...out];
  }

  select(ids: string[], mode: "replace" | "add" | "toggle" = "replace"): void {
    const expanded = this.expandGroups(ids);
    let next: string[];
    if (mode === "replace") next = expanded;
    else if (mode === "add") next = [...new Set([...this.ui.selection, ...expanded])];
    else {
      const cur = new Set(this.ui.selection);
      const allIn = expanded.every((id) => cur.has(id));
      for (const id of expanded) allIn ? cur.delete(id) : cur.add(id);
      next = [...cur];
    }
    if (this.ui.enteredGroup && !next.some((id) => this.doc.get(id)?.groupId === this.ui.enteredGroup)) {
      this.setUi({ enteredGroup: null });
    }
    this.setUi({ selection: next });
  }

  clearSelection(): void {
    if (this.ui.selection.length || this.ui.enteredGroup) this.setUi({ selection: [], enteredGroup: null });
  }

  selectAll(): void {
    this.setUi({ selection: this.doc.all().filter((e) => this.isEditable(e)).map((e) => e.id), enteredGroup: null });
  }

  selectionBounds(): Box | null {
    return unionBoxes(this.selected().map(bounds));
  }

  /** The box selection handles wrap: the element's own rotated frame for a single element. */
  selectionFrame(): { box: Box; rot: number } | null {
    const sel = this.selected();
    if (!sel.length) return null;
    if (sel.length === 1 && sel[0].type !== "line") {
      const el = sel[0];
      const b = frameBox(el);
      if (el.type === "ink") return { box: { x: b.x - el.size / 2, y: b.y - el.size / 2, w: b.w + el.size, h: b.h + el.size }, rot: el.rot };
      return { box: b, rot: el.rot };
    }
    return { box: unionBoxes(sel.map(bounds))!, rot: 0 };
  }

  // ---------- hit testing ----------

  /**
   * Topmost element under the point. Painted parts win; failing that, the inside of a hollow shape,
   * and last of all a frame (so ink drawn inside a box or a frame stays easy to pick).
   */
  hitAt(p: Vec, tolScreen = 8, opts: { includeLocked?: boolean; filter?: (e: SepiaElement) => boolean; precise?: boolean } = {}): SepiaElement | null {
    const tol = tolScreen / this.camera.z;
    const all = this.doc.all();
    let hollowHit: SepiaElement | null = null;
    let frameHit: SepiaElement | null = null;
    for (let i = all.length - 1; i >= 0; i--) {
      const el = this.getEl(all[i].id)!;
      if (el.locked && !opts.includeLocked) continue;
      // hidden and locked layers can't be touched at all
      if (!this.isOnOpenLayer(el)) continue;
      if (opts.filter && !opts.filter(el)) continue;
      if (hitTest(el, p, tol, opts.precise)) {
        if (el.type === "frame") {
          frameHit ??= el;
          continue;
        }
        return el;
      }
      if (!opts.precise && !hollowHit && hitHollow(el, p)) hollowHit = el;
    }
    return hollowHit ?? frameHit;
  }

  elementsInBox(box: Box): SepiaElement[] {
    return this.doc.all().filter((e) => this.isEditable(e) && intersectsBox(e, box));
  }

  // ---------- editing text ----------

  startEditing(id: string): void {
    const el = this.doc.get(id);
    if (!el || this.ui.readOnly || el.locked) return;
    if (!["text", "note", "card", "shape", "line", "frame", "image"].includes(el.type)) return;
    this.focusProxy?.();
    this.setUi({ editingId: id, selection: [id] });
  }

  stopEditing(): void {
    const id = this.ui.editingId;
    if (!id) return;
    this.setUi({ editingId: null });
    this.editEndedAt = performance.now();
    // an empty text box disappears when you leave it
    const el = this.doc.get(id);
    if (el && el.type === "text" && !el.text.trim()) this.commit({ dels: [id] }, { history: false });
    else if (el?.type === "text") this.lastTextId = id;
    (document.activeElement as HTMLElement | null)?.blur?.();
  }

  // ---------- commands ----------

  update(id: string, fn: (el: SepiaElement) => SepiaElement, key?: string): void {
    const el = this.doc.get(id);
    if (!el) return;
    const next = fn(el);
    if (next !== el) this.commit({ puts: [{ ...next, t: Date.now() }] }, { key });
  }

  updateSelected(fn: (el: SepiaElement) => SepiaElement | null, key?: string): void {
    const puts: SepiaElement[] = [];
    for (const el of this.selected()) {
      const next = fn(el);
      if (next && next !== el) puts.push({ ...next, t: Date.now() });
    }
    if (puts.length) this.commit({ puts }, { key });
  }

  deleteSelected(): void {
    const ids = this.ui.selection.filter((id) => !this.doc.get(id)?.locked);
    if (!ids.length) return;
    this.commit({ dels: ids });
    this.setUi({ selection: [], editingId: null });
  }

  /** Copies of elements with fresh ids, offset, re-linked to each other (connectors, groups). */
  cloneElements(els: SepiaElement[], dx: number, dy: number, layer?: string): SepiaElement[] {
    const idMap = new Map<string, string>();
    const groupMap = new Map<string, string>();
    for (const el of els) idMap.set(el.id, newId());
    const zs = this.zTop(els.length);
    const sorted = [...els].sort((a, b) => (a.z < b.z ? -1 : 1));
    return sorted.map((el, i) => {
      const copy = { ...el, id: idMap.get(el.id)!, z: zs[i], x: el.x + dx, y: el.y + dy, t: Date.now(), locked: false } as SepiaElement;
      if (layer !== undefined) copy.layer = layer;
      if (el.groupId) {
        if (!groupMap.has(el.groupId)) groupMap.set(el.groupId, newId());
        copy.groupId = groupMap.get(el.groupId)!;
      }
      if (copy.type === "line") {
        copy.start = { ...copy.start, bind: copy.start.bind ? idMap.get(copy.start.bind) ?? null : null };
        copy.end = { ...copy.end, bind: copy.end.bind ? idMap.get(copy.end.bind) ?? null : null };
      }
      return copy;
    });
  }

  duplicateSelected(): void {
    const sel = this.selected();
    if (!sel.length) return;
    const off = 24 / Math.max(this.camera.z, 0.25);
    const copies = this.cloneElements(sel, off, off);
    this.commit({ puts: copies });
    this.setUi({ selection: copies.map((c) => c.id) });
  }

  copy(): void {
    const sel = this.selected();
    if (!sel.length) return;
    this.clipboard = sel.map((e) => ({ ...e }));
    const payload = JSON.stringify({ [CLIP_MARK]: true, els: this.clipboard });
    navigator.clipboard?.writeText(payload).catch(() => undefined);
  }

  cut(): void {
    this.copy();
    this.deleteSelected();
  }

  /** Paste Sepia elements (from our clipboard or JSON text) centred on the view. */
  pasteElements(els: SepiaElement[] | null = null, at?: Vec): void {
    const src = (els ?? this.clipboard).filter((e) => e.type !== "layer" && e.type !== "page");
    if (!src.length || this.ui.readOnly || !this.canDrawOnActiveLayer()) return;
    const b = unionBoxes(src.map(bounds))!;
    const target = this.placementPoint(at);
    const copies = this.cloneElements(src, target.x - (b.x + b.w / 2), target.y - (b.y + b.h / 2), this.activeLayerId());
    this.commit({ puts: copies });
    this.setUi({ selection: copies.map((c) => c.id) });
  }

  parseClipboardText(text: string): SepiaElement[] | null {
    if (!text.includes(CLIP_MARK)) return null;
    try {
      const data = JSON.parse(text);
      if (data && data[CLIP_MARK] && Array.isArray(data.els)) return data.els as SepiaElement[];
    } catch {
      /* not ours */
    }
    return null;
  }

  insert(els: SepiaElement[], select = true): void {
    if (!els.length || !this.canDrawOnActiveLayer()) return;
    const zs = this.zTop(els.length);
    const layer = this.activeLayerId();
    const withZ = els.map((e, i) => ({ ...e, z: zs[i], t: Date.now(), layer }) as SepiaElement);
    this.commit({ puts: withZ });
    if (select) this.setUi({ selection: withZ.map((e) => e.id) });
  }

  transformSelected(t: Xform, key?: string): void {
    const puts = this.selected().filter((e) => !e.locked).map((e) => ({ ...transformElement(e, t), t: Date.now() }));
    if (puts.length) this.commit({ puts }, { key });
  }

  nudge(dx: number, dy: number): void {
    this.transformSelected({ kind: "move", dx, dy }, "nudge");
  }

  /** Bring forward / send back, within each element's own layer. */
  reorder(where: "front" | "forward" | "backward" | "back"): void {
    const sel = new Set(this.ui.selection);
    if (!sel.size) return;
    const byLayer = new Map<string, DrawElement[]>();
    for (const e of this.doc.all()) {
      const l = this.layerOf(e);
      if (!byLayer.has(l)) byLayer.set(l, []);
      byLayer.get(l)!.push(e);
    }
    const puts: SepiaElement[] = [];
    for (const all of byLayer.values()) {
      if (!all.some((e) => sel.has(e.id))) continue;
      const chosen = all.filter((e) => sel.has(e.id));
      const rest = all.filter((e) => !sel.has(e.id));
      let order: SepiaElement[];
      if (where === "front") order = [...rest, ...chosen];
      else if (where === "back") order = [...chosen, ...rest];
      else {
        order = [...all];
        const idxs = order.map((e, i) => (sel.has(e.id) ? i : -1)).filter((i) => i >= 0);
        if (where === "forward") {
          for (let k = idxs.length - 1; k >= 0; k--) {
            const i = idxs[k];
            if (i < order.length - 1 && !sel.has(order[i + 1].id)) [order[i], order[i + 1]] = [order[i + 1], order[i]];
          }
        } else {
          for (const i of idxs) if (i > 0 && !sel.has(order[i - 1].id)) [order[i], order[i - 1]] = [order[i - 1], order[i]];
        }
      }
      // keep existing keys where the order still holds; give moved elements keys between their new neighbours
      let prev: string | null = null;
      for (let i = 0; i < order.length; i++) {
        const el = order[i];
        if (!sel.has(el.id) && (prev === null || el.z > prev)) {
          prev = el.z;
          continue;
        }
        let hi: string | null = null;
        for (let j = i + 1; j < order.length; j++) {
          const z = order[j].z;
          if (!sel.has(order[j].id) && (prev === null || z > prev)) {
            hi = z;
            break;
          }
        }
        const z = generateKeyBetween(prev, hi);
        puts.push({ ...el, z, t: Date.now() });
        prev = z;
      }
    }
    if (puts.length) this.commit({ puts });
  }

  group(): void {
    const sel = this.selected();
    if (sel.length < 2) return;
    const gid = newId();
    this.commit({ puts: sel.map((e) => ({ ...e, groupId: gid, t: Date.now() })) });
  }

  ungroup(): void {
    const sel = this.selected().filter((e) => e.groupId);
    if (!sel.length) return;
    this.commit({ puts: sel.map((e) => ({ ...e, groupId: null, t: Date.now() })) });
  }

  toggleLock(): void {
    const sel = this.selected();
    if (!sel.length) return;
    const lock = !sel.every((e) => e.locked);
    this.commit({ puts: sel.map((e) => ({ ...e, locked: lock, t: Date.now() })) });
    if (lock) this.clearSelection();
  }

  align(kind: "left" | "hcenter" | "right" | "top" | "vcenter" | "bottom"): void {
    const sel = this.selected().filter((e) => !e.locked);
    if (sel.length < 2) return;
    const all = unionBoxes(sel.map(bounds))!;
    this.commit({
      puts: sel.map((e) => {
        const b = bounds(e);
        let dx = 0;
        let dy = 0;
        if (kind === "left") dx = all.x - b.x;
        if (kind === "right") dx = all.x + all.w - (b.x + b.w);
        if (kind === "hcenter") dx = all.x + all.w / 2 - (b.x + b.w / 2);
        if (kind === "top") dy = all.y - b.y;
        if (kind === "bottom") dy = all.y + all.h - (b.y + b.h);
        if (kind === "vcenter") dy = all.y + all.h / 2 - (b.y + b.h / 2);
        return { ...transformElement(e, { kind: "move", dx, dy }), t: Date.now() };
      })
    });
  }

  distribute(axis: "x" | "y"): void {
    const sel = this.selected().filter((e) => !e.locked);
    if (sel.length < 3) return;
    const items = sel.map((e) => ({ e, b: bounds(e) })).sort((a, b) => (axis === "x" ? a.b.x - b.b.x : a.b.y - b.b.y));
    const first = items[0].b;
    const last = items[items.length - 1].b;
    const total = axis === "x" ? last.x + last.w - first.x : last.y + last.h - first.y;
    const sizes = items.reduce((s, it) => s + (axis === "x" ? it.b.w : it.b.h), 0);
    const gap = (total - sizes) / (items.length - 1);
    let cursor = axis === "x" ? first.x : first.y;
    const puts: SepiaElement[] = [];
    for (const it of items) {
      const d = cursor - (axis === "x" ? it.b.x : it.b.y);
      puts.push({ ...transformElement(it.e, axis === "x" ? { kind: "move", dx: d, dy: 0 } : { kind: "move", dx: 0, dy: d }), t: Date.now() });
      cursor += (axis === "x" ? it.b.w : it.b.h) + gap;
    }
    this.commit({ puts });
  }

  /** Elements fully inside a frame (they travel with it). */
  frameChildren(frameId: string): SepiaElement[] {
    const f = this.doc.get(frameId);
    if (!f || f.type !== "frame") return [];
    const box = boxOf(f);
    return this.doc.all().filter((e) => e.id !== frameId && e.type !== "frame" && boxContains(box, bounds(e)));
  }

  // ---------- layers ----------

  /** The base layer as it would look if it were never customised (one shared object, so UI snapshots stay stable). */
  private static readonly VIRTUAL_BASE: LayerElement = Object.freeze({
    id: BASE_LAYER, type: "layer", name: "Layer 1", visible: true, locked: false, blend: "normal", x: 0, y: 0, w: 0, h: 0, rot: 0, z: "", t: 0
  }) as LayerElement;

  private virtualBase(): LayerElement {
    return Editor.VIRTUAL_BASE;
  }

  /**
   * Layers bottom to top. The base layer is listed even when it was never stored, as long as something is on it
   * (or there are no other layers).
   */
  layers(): LayerElement[] {
    const stored = this.doc.layers();
    if (stored.some((l) => l.id === BASE_LAYER)) return stored;
    const known = new Set(stored.map((l) => l.id));
    const baseUsed = !stored.length || this.doc.all().some((e) => !e.layer || !known.has(e.layer));
    return baseUsed ? [this.virtualBase(), ...stored] : stored;
  }

  layerById(id: string): LayerElement | undefined {
    return this.layers().find((l) => l.id === id);
  }

  /** The layer an element is drawn on. */
  layerOf(el: SepiaElement): string {
    const id = el.layer || BASE_LAYER;
    if (id !== BASE_LAYER && this.doc.get(id)?.type !== "layer") return BASE_LAYER;
    return id;
  }

  private layerState(id: string): LayerElement | undefined {
    const stored = this.doc.get(id);
    if (stored?.type === "layer") return stored;
    return id === BASE_LAYER ? this.virtualBase() : undefined;
  }

  /** On a visible layer. */
  isShown(el: SepiaElement): boolean {
    return this.layerState(this.layerOf(el))?.visible !== false;
  }

  /** On a visible, unlocked layer. */
  isOnOpenLayer(el: SepiaElement): boolean {
    const l = this.layerState(this.layerOf(el));
    return !!l && l.visible && !l.locked;
  }

  /** Can be selected, moved, restyled or erased. */
  isEditable(el: SepiaElement): boolean {
    return !el.locked && this.isOnOpenLayer(el);
  }

  /** What the eraser and scribble-to-erase may remove: like Photoshop, only the active layer unless told otherwise. */
  isErasable(el: SepiaElement): boolean {
    return this.isEditable(el) && (this.style.eraseAllLayers || this.layerOf(el) === this.activeLayerId());
  }

  /** The layer new things go on (falls back to the top layer if the chosen one disappeared). */
  activeLayerId(): string {
    const layers = this.layers();
    if (layers.some((l) => l.id === this.ui.activeLayer)) return this.ui.activeLayer;
    return layers[layers.length - 1]?.id ?? BASE_LAYER;
  }

  setActiveLayer(id: string): void {
    this.setUi({ activeLayer: id });
  }

  /** New things can't go on a hidden or locked layer: say so instead of drawing invisibly. */
  canDrawOnActiveLayer(quiet = false): boolean {
    const l = this.layerState(this.activeLayerId());
    if (!l || (l.visible && !l.locked)) return true;
    if (!quiet) this.host.toast(`“${l.name}” is ${!l.visible ? "hidden" : "locked"}. Show or unlock it, or pick another layer.`);
    return false;
  }

  /** Store the base layer as a real element the first time it is customised or ordered. */
  private materialisedBase(): LayerElement | null {
    if (this.doc.get(BASE_LAYER)?.type === "layer") return null;
    const first = this.doc.layers()[0]?.z ?? null;
    return { ...this.virtualBase(), z: generateKeyBetween(null, first), t: Date.now() };
  }

  private freshLayerName(): string {
    const names = new Set(this.layers().map((l) => l.name));
    let n = this.layers().length + 1;
    while (names.has(`Layer ${n}`)) n++;
    return `Layer ${n}`;
  }

  /** A new layer right above the active one (or above `opts.above`); it becomes the active layer. */
  addLayer(opts: { name?: string; above?: string; props?: Partial<Pick<LayerElement, "visible" | "locked" | "opacity" | "blend">> } = {}): string {
    if (this.ui.readOnly) return this.activeLayerId();
    const puts: SepiaElement[] = [];
    const base = this.materialisedBase();
    if (base) puts.push(base);
    const layers = [...this.doc.layers(), ...(base ? [base] : [])].sort((a, b) => (a.z < b.z ? -1 : 1));
    const anchor = opts.above ?? this.activeLayerId();
    const i = layers.findIndex((l) => l.id === anchor);
    const below = i >= 0 ? layers[i].z : layers[layers.length - 1]?.z ?? null;
    const above = i >= 0 ? layers[i + 1]?.z ?? null : null;
    const layer: LayerElement = {
      id: newId(), type: "layer", name: opts.name || this.freshLayerName(), visible: true, locked: false, blend: "normal",
      x: 0, y: 0, w: 0, h: 0, rot: 0, z: generateKeyBetween(below, above), t: Date.now(), ...opts.props
    };
    puts.push(layer);
    this.commit({ puts }, { key: "add-layer-" + layer.id });
    this.setActiveLayer(layer.id);
    return layer.id;
  }

  updateLayer(id: string, patch: Partial<Pick<LayerElement, "name" | "visible" | "locked" | "opacity" | "blend">>, key?: string): void {
    if (this.ui.readOnly) return;
    const cur = this.layerState(id);
    if (!cur) return;
    const stored = this.doc.get(id)?.type === "layer";
    const base = !stored && id === BASE_LAYER ? this.materialisedBase() : null;
    const next = { ...(base ?? cur), ...patch, t: Date.now() } as LayerElement;
    this.commit({ puts: [next] }, { key });
    // whatever can no longer be touched leaves the selection
    if (patch.visible === false || patch.locked) this.setUi({ selection: this.ui.selection.filter((sid) => this.layerOf(this.doc.get(sid)!) !== id) });
  }

  /** Move a layer to `index` in the bottom-to-top list. */
  moveLayer(id: string, index: number): void {
    if (this.ui.readOnly) return;
    const puts: SepiaElement[] = [];
    const base = this.materialisedBase();
    let layers = this.layers().map((l) => (l.id === BASE_LAYER && base ? base : l));
    if (base) puts.push(base);
    const moving = layers.find((l) => l.id === id);
    if (!moving) return;
    layers = layers.filter((l) => l.id !== id);
    const at = Math.max(0, Math.min(layers.length, index));
    const lo = layers[at - 1]?.z ?? null;
    const hi = layers[at]?.z ?? null;
    if (lo !== null && hi !== null && lo >= hi) return;
    const z = generateKeyBetween(lo, hi);
    const moved = { ...moving, z, t: Date.now() };
    const i = puts.findIndex((p) => p.id === id);
    if (i >= 0) puts[i] = moved;
    else puts.push(moved);
    this.commit({ puts });
  }

  /** Elements on a layer, back to front. */
  layerContents(id: string): DrawElement[] {
    return this.doc.all().filter((e) => this.layerOf(e) === id);
  }

  /** Delete a layer and everything on it. The last layer can't go. */
  deleteLayer(id: string): void {
    if (this.ui.readOnly || this.layers().length <= 1) return;
    const dels = this.layerContents(id).map((e) => e.id);
    if (this.doc.get(id)?.type === "layer") dels.push(id);
    this.commit({ dels });
    if (this.ui.activeLayer === id) {
      const rest = this.layers().filter((l) => l.id !== id);
      this.setActiveLayer(rest[rest.length - 1]?.id ?? BASE_LAYER);
    }
  }

  /** A copy of the layer (and everything on it) right above it, in one undo step. */
  duplicateLayer(id: string): void {
    const src = this.layerState(id);
    if (!src || this.ui.readOnly) return;
    const copyId = this.addLayer({ name: `${src.name} copy`, above: id, props: { visible: src.visible, blend: src.blend, opacity: src.opacity } });
    const copies = this.cloneElements(this.layerContents(id), 0, 0, copyId);
    if (copies.length) this.commit({ puts: copies }, { key: "add-layer-" + copyId });
    this.history.seal();
  }

  /** Fold a layer into the one below it (its opacity is baked into the elements). */
  mergeDown(id: string): void {
    if (this.ui.readOnly) return;
    const layers = this.layers();
    const i = layers.findIndex((l) => l.id === id);
    if (i <= 0) return;
    const target = layers[i - 1];
    const upper = layers[i];
    const k = upper.opacity ?? 1;
    const moving = this.layerContents(id);
    const zs = this.zTop(moving.length);
    const puts: SepiaElement[] = moving.map((e, j) => ({ ...e, layer: target.id, z: zs[j], opacity: k === 1 ? e.opacity : (e.opacity ?? 1) * k, t: Date.now() }) as SepiaElement);
    const dels = this.doc.get(id)?.type === "layer" ? [id] : [];
    this.commit({ puts, dels });
    this.setActiveLayer(target.id);
  }

  /** Send the selection to another layer (on top of what is already there). */
  moveSelectionToLayer(id: string): void {
    const sel = this.selected().filter((e) => e.type !== "layer").sort((a, b) => (a.z < b.z ? -1 : 1));
    if (!sel.length || this.ui.readOnly) return;
    const zs = this.zTop(sel.length);
    this.commit({ puts: sel.map((e, i) => ({ ...e, layer: id, z: zs[i], t: Date.now() }) as SepiaElement) });
    const l = this.layerState(id);
    if (l && (!l.visible || l.locked)) this.clearSelection();
  }

  /** Select everything on a layer. */
  selectLayer(id: string): void {
    this.setUi({ selection: this.layerContents(id).filter((e) => this.isEditable(e)).map((e) => e.id), enteredGroup: null });
  }

  setLayerBlend(id: string, blend: BlendMode): void {
    this.updateLayer(id, { blend });
  }

  // ---------- pages (paged boards) ----------

  /** A board with pages is a notebook: you write on pages stacked top to bottom, and can add more. */
  isPaged(): boolean {
    return this.doc.pages().length > 0;
  }

  pages(): PageElement[] {
    return this.doc.pages();
  }

  private pagesBoxCache: { v: number; box: Box | null } = { v: -1, box: null };

  /** Everything the pages cover (null on an endless board). */
  pagesBox(): Box | null {
    if (this.pagesBoxCache.v !== this.doc.structureVersion) {
      const pages = this.doc.pages();
      this.pagesBoxCache = { v: this.doc.structureVersion, box: pages.length ? unionBoxes(pages.map(boxOf)) : null };
    }
    return this.pagesBoxCache.box;
  }

  /** The page under a world point. */
  pageAt(p: Vec, slack = 0): PageElement | undefined {
    return this.doc.pages().find((pg) => p.x >= pg.x - slack && p.x <= pg.x + pg.w + slack && p.y >= pg.y - slack && p.y <= pg.y + pg.h + slack);
  }

  /** The page in the middle of the view, or the closest one. */
  currentPage(): PageElement | undefined {
    const pages = this.doc.pages();
    const v = this.camera.view();
    const c = { x: v.x + v.w / 2, y: v.y + v.h / 2 };
    let best: PageElement | undefined;
    let bestD = Infinity;
    for (const pg of pages) {
      const dx = Math.max(pg.x - c.x, 0, c.x - pg.x - pg.w);
      const dy = Math.max(pg.y - c.y, 0, c.y - pg.y - pg.h);
      if (dx * dx + dy * dy < bestD) {
        bestD = dx * dx + dy * dy;
        best = pg;
      }
    }
    return best;
  }

  /** On a paged board only the pages take ink: the desk around them doesn't. */
  isOnPaper(p: Vec): boolean {
    return !this.isPaged() || !!this.pageAt(p);
  }

  private deskHintAt = -Infinity;

  /** Explain (now and then) why nothing happens when writing on the desk. */
  deskHint(): void {
    if (performance.now() - this.deskHintAt < 15000) return;
    this.deskHintAt = performance.now();
    this.host.toast("That's the desk — write on a page. Scroll to the end for ＋ Add page.");
  }

  /** Where something new should go: the given point (or the middle of the view), moved onto a page if needed. */
  placementPoint(at?: Vec): Vec {
    const p = at ?? center(this.camera.view());
    if (!this.isPaged() || this.pageAt(p)) return p;
    const pg = this.currentPage()!;
    const m = Math.min(60, pg.w / 4, pg.h / 4);
    return { x: Math.min(pg.x + pg.w - m, Math.max(pg.x + m, p.x)), y: Math.min(pg.y + pg.h - m, Math.max(pg.y + m, p.y)) };
  }

  /** What is written on a page (everything whose middle is on it, on every layer). */
  pageContents(pg: PageElement): DrawElement[] {
    return this.doc.all().filter((e) => {
      const b = bounds(e);
      const cx = b.x + b.w / 2;
      const cy = b.y + b.h / 2;
      return cx >= pg.x && cx <= pg.x + pg.w && cy >= pg.y && cy <= pg.y + pg.h;
    });
  }

  /**
   * Stack pages top to bottom in the given order, starting at `top` and centred on the first page's axis.
   * What is written on a page moves with it. `fresh` pages are new and have nothing on them yet.
   */
  private stackPages(order: PageElement[], top: number, fresh: PageElement[] = []): SepiaElement[] {
    const freshIds = new Set(fresh.map((p) => p.id));
    const first = this.doc.pages()[0] ?? order[0];
    const axis = first.x + first.w / 2;
    const contents = new Map<string, DrawElement[]>();
    for (const pg of order) if (!freshIds.has(pg.id)) contents.set(pg.id, this.pageContents(pg));
    const now = Date.now();
    const puts: SepiaElement[] = [];
    let y = top;
    for (const pg of order) {
      const x = axis - pg.w / 2;
      const dx = x - pg.x;
      const dy = y - pg.y;
      if (freshIds.has(pg.id) || dx || dy) puts.push({ ...pg, x, y, t: now });
      if (!freshIds.has(pg.id) && (dx || dy)) {
        for (const el of contents.get(pg.id)!) puts.push({ ...transformElement(el, { kind: "move", dx, dy }), t: now });
      }
      y += pg.h + PAGE_GAP;
    }
    return puts;
  }

  /** A blank page after the given one (default: at the end), the same size and ruling. Returns its id. */
  addPage(afterId?: string): string | null {
    const pages = this.doc.pages();
    if (!pages.length || this.ui.readOnly) return null;
    const anchor = pages.find((p) => p.id === afterId) ?? pages[pages.length - 1];
    const page: PageElement = { ...anchor, id: newId(), t: Date.now() };
    const order = [...pages];
    order.splice(order.indexOf(anchor) + 1, 0, page);
    this.commit({ puts: this.stackPages(order, pages[0].y, [page]) });
    return page.id;
  }

  /** A copy of a page and everything on it, right after it. Returns the new page's id. */
  duplicatePage(id: string): string | null {
    const pages = this.doc.pages();
    const pg = pages.find((p) => p.id === id);
    if (!pg || this.ui.readOnly) return null;
    const copy: PageElement = { ...pg, id: newId(), t: Date.now() };
    const order = [...pages];
    order.splice(order.indexOf(pg) + 1, 0, copy);
    const puts = this.stackPages(order, pages[0].y, [copy]);
    const placed = puts.find((p) => p.id === copy.id)!;
    const clones = this.cloneElements(this.pageContents(pg), placed.x - pg.x, placed.y - pg.y);
    this.commit({ puts: [...puts, ...clones] });
    return copy.id;
  }

  /** Remove a page and what is written on it; the pages below move up. A notebook keeps at least one page. */
  deletePage(id: string): boolean {
    const pages = this.doc.pages();
    const pg = pages.find((p) => p.id === id);
    if (!pg || this.ui.readOnly) return false;
    if (pages.length < 2) {
      this.host.toast("A notebook keeps at least one page.");
      return false;
    }
    const gone = this.pageContents(pg).map((e) => e.id);
    const puts = this.stackPages(pages.filter((p) => p.id !== id), pages[0].y);
    this.commit({ puts, dels: [id, ...gone] });
    const goneSet = new Set(gone);
    this.setUi({ selection: this.ui.selection.filter((s) => !goneSet.has(s)), editingId: null });
    return true;
  }

  /** Move a page to another position (0 = first), taking what is written on it along. */
  movePage(id: string, to: number): void {
    const pages = this.doc.pages();
    const from = pages.findIndex((p) => p.id === id);
    if (from < 0 || this.ui.readOnly) return;
    to = Math.max(0, Math.min(pages.length - 1, to));
    if (to === from) return;
    const order = [...pages];
    const [pg] = order.splice(from, 1);
    order.splice(to, 0, pg);
    this.commit({ puts: this.stackPages(order, pages[0].y) });
  }

  /** Change one page's ruling (null: use the board's). */
  setPagePattern(id: string, pattern: PatternKey | null): void {
    const pg = this.doc.get(id);
    if (pg?.type !== "page") return;
    this.commit({ puts: [{ ...pg, pattern, t: Date.now() }] });
  }

  /** Show a whole page. */
  goToPage(id: string, animate = true): void {
    const pg = this.doc.get(id);
    if (pg?.type !== "page") return;
    const t = this.camera.fitting(boxOf(pg), 28, 3);
    animate ? this.camera.animateTo(t) : this.camera.set(t.x, t.y, t.z);
  }

  /** Show a page across the free width of the screen, from its top: ready to write. */
  fitPageWidth(id: string, animate = false): void {
    const pg = this.doc.get(id);
    if (pg?.type !== "page") return;
    const cam = this.camera;
    const i = cam.insets;
    const free = Math.max(80, cam.width - i.left - i.right - 40);
    // about 130% at most: a comfortable size for handwriting between the rules
    const z = Math.min(1.3, Math.max(0.1, free / pg.w));
    const t = { x: i.left + (cam.width - i.left - i.right) / 2 - (pg.x + pg.w / 2) * z, y: i.top + 18 - pg.y * z, z };
    animate ? cam.animateTo(t) : cam.set(t.x, t.y, t.z);
  }

  // ---------- camera helpers ----------

  zoomBy(factor: number): void {
    this.camera.animateTo(this.zoomTarget(this.camera.z * factor), 180);
  }

  private zoomTarget(z: number) {
    const cam = this.camera;
    const nz = Math.min(24, Math.max(0.04, z));
    const cx = cam.width / 2;
    const cy = cam.height / 2;
    const k = nz / cam.z;
    return { x: cx - (cx - cam.x) * k, y: cy - (cy - cam.y) * k, z: nz };
  }

  resetZoom(): void {
    this.camera.animateTo(this.zoomTarget(1), 220);
  }

  zoomToFit(animate = true): void {
    const page = this.currentPage();
    if (page) return this.goToPage(page.id, animate);
    const all = this.doc.all().filter((e) => this.isShown(e));
    if (!all.length) {
      const t = { x: this.camera.width / 2, y: this.camera.height / 2, z: 1 };
      animate ? this.camera.animateTo(t) : this.camera.set(t.x, t.y, t.z);
      return;
    }
    const box = unionBoxes(all.map(bounds))!;
    const t = this.camera.fitting(box, 70, 1.5);
    animate ? this.camera.animateTo(t) : this.camera.set(t.x, t.y, t.z);
  }

  zoomToSelection(): void {
    const b = this.selectionBounds();
    if (b) this.camera.animateTo(this.camera.fitting(b, 90, 3));
  }

  zoomToBox(b: Box, maxZoom = 3): void {
    this.camera.animateTo(this.camera.fitting(b, 50, maxZoom));
  }

  /** Plain text of the whole board, for search. */
  searchText(): string {
    return this.doc
      .all()
      .map(elementText)
      .filter((t) => t.trim())
      .join("\n")
      .slice(0, 20000);
  }
}
